import { useViewport } from 'contexts/viewport';
import { useTranslation } from 'react-i18next';
import { Box, Button, Form, Icon, PasswordInput, Stack, styled, Text } from 'shell/components';

const PasswordInputs = ({ form, errors, satisfyRequirement, handlePrev, handleNext, handleChange }) => {
    const { device } = useViewport();
    const { t } = useTranslation();

    return (
        <RootBox device={device}>
            <Stack alignItems={'center'}>
                <Text component={'h1'} variant={'h6'}>
                    {t('auth:sign-up.nice-to-meet-you', 'Nice to meet you {{name}}!', {
                        name: form.preferredName || form.firstName,
                    })}
                </Text>
                <Text sx={{ mt: 1 }} variant={'p1'} align={'center'}>
                    {t('auth:sign-up.create-password-step', 'Next, let’s create a password to protect your account.')}
                </Text>
            </Stack>
            <Form onSubmit={handleNext}>
                <InputStack spacing={4}>
                    <PasswordInput
                        autoComplete={'off'}
                        data-cy={'account-creation-enter-password'}
                        error={Boolean(errors.password)}
                        helperText={errors.password}
                        label={t('account-settings.password.enter-new-password', 'Enter New Password')}
                        placeholder={t('account-settings.password.enter-new-password', 'Enter New Password')}
                        required
                        value={form.password}
                        onChange={(e) => handleChange('password', e.target.value)}
                    />

                    <Stack sx={{ margin: (theme) => theme.spacing(2, 0, 6, 0) }}>
                        <Text variant={'p3'}>The password must have:</Text>
                        <Text
                            variant={'p3'}
                            color={!satisfyRequirement.validateMoreThan8Characters ? 'light' : 'error'}
                        >
                            • {t('account-settings.password.req-one-min-characters', 'At least 8 characters')}
                        </Text>
                        <Text variant={'p3'} color={!satisfyRequirement.validateOneLowerCase ? 'light' : 'error'}>
                            • {t('account-settings.password.req-three-lowercase', 'At least one lowercase letter')}
                        </Text>
                        <Text variant={'p3'} color={!satisfyRequirement.validateOneUpperCase ? 'light' : 'error'}>
                            • {t('account-settings.password.req-two-uppercase', 'At least one uppercase letter')}
                        </Text>
                        <Text variant={'p3'} color={!satisfyRequirement.validateOneNumber ? 'light' : 'error'}>
                            • {t('account-settings.password.req-four-number', 'At least one number')}
                        </Text>
                        <Text variant={'p3'} color={!satisfyRequirement.validateOneSpecial ? 'light' : 'error'}>
                            • {t('account-settings.password.req-five-symbol', 'At least one special character')}
                        </Text>
                    </Stack>
                    <PasswordInput
                        autoComplete={'off'}
                        data-cy={'account-creation-confirm-password'}
                        error={Boolean(errors.confirmPassword)}
                        helperText={errors.confirmPassword}
                        label={t('account-settings.password.confirm-password', 'Confirm Password')}
                        placeholder={t('account-settings.password.enter-new-password', 'Enter New Password')}
                        required
                        value={form.confirmPassword}
                        onChange={(e) => handleChange('confirmPassword', e.target.value)}
                    />
                </InputStack>
                <ButtonStack direction={'row'} justifyContent={'space-between'}>
                    <Button
                        color={'neutral'}
                        startIcon={<Icon kind={'arrowLeft'} />}
                        variant={'tertiary'}
                        onClick={handlePrev}
                    >
                        {t('controls.back', 'Back')}
                    </Button>
                    <Button data-cy={'account-creation-next-button'} type={'submit'}>
                        {t('controls.complete', 'Complete')}
                    </Button>
                </ButtonStack>
            </Form>
        </RootBox>
    );
};

const RootBox = styled(Box)(({ theme, device }) => {
    return {
        marginTop: theme.spacing(12),
        padding: device === 'mobile' ? 0 : theme.spacing(7),
        width: device === 'mobile' ? '100%' : 450,
        height: 'fit-content',
        background: device === 'mobile' ? 'transparent' : theme.palette.ensightful.shades[0],
        boxShadow: device === 'mobile' ? 'none' : theme.palette.ensightful.shadows[1],
        borderRadius: '12px',
    };
});

const InputStack = styled(Stack)(({ theme }) => {
    return {
        margin: theme.spacing(6, 0, 2, 0),
    };
});

const ButtonStack = styled(Stack)(({ theme }) => {
    return {
        margin: theme.spacing(6, 0, 3, 0),
    };
});

export default PasswordInputs;
